'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  registerUserScopedStore,
  userScopedStorage,
} from '@/shared/lib/user-scoped-storage';

export type FeedbackVariant = 'positive' | 'filtered';

const keyOf = (renderId: string, variant: FeedbackVariant) =>
  `${renderId}:${variant}`;

interface FeedbackCollapseState {
  collapsedIds: string[];
  isCollapsed: (renderId: string, variant: FeedbackVariant) => boolean;
  setCollapsed: (
    renderId: string,
    variant: FeedbackVariant,
    collapsed: boolean
  ) => void;
  toggle: (renderId: string, variant: FeedbackVariant) => void;
}

export const useFeedbackCollapseStore = create<FeedbackCollapseState>()(
  persist(
    (set, get) => ({
      collapsedIds: [],
      isCollapsed: (renderId, variant) =>
        get().collapsedIds.includes(keyOf(renderId, variant)),
      setCollapsed: (renderId, variant, collapsed) => {
        const key = keyOf(renderId, variant);
        set((state) => {
          const has = state.collapsedIds.includes(key);
          if (collapsed && !has) {
            return { collapsedIds: [...state.collapsedIds, key] };
          }
          if (!collapsed && has) {
            return {
              collapsedIds: state.collapsedIds.filter((k) => k !== key),
            };
          }
          return state;
        });
      },
      toggle: (renderId, variant) => {
        const key = keyOf(renderId, variant);
        set((state) => ({
          collapsedIds: state.collapsedIds.includes(key)
            ? state.collapsedIds.filter((k) => k !== key)
            : [...state.collapsedIds, key],
        }));
      },
    }),
    {
      name: 'render-feedback-collapse',
      // User-scoped key prefix: see `user-scoped-storage.ts`.
      storage: createJSONStorage(() => userScopedStorage()),
    }
  )
);

registerUserScopedStore(useFeedbackCollapseStore);
